import type { FC } from 'react'
import { useMemo } from 'react'
import type { Asset } from 'contentful'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import { useRecentEpisodeQuery } from '@segment/api/routes/goodDataBetterMarketing/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Recent episodes`,
}

export type RecentEpisodesProps = {
  episodeSlug: string
}

const RecentEpisodes: FC<RecentEpisodesProps> = ({ episodeSlug }) => {
  const { data } = useRecentEpisodeQuery({ args: [episodeSlug] })

  return (
    <AppearanceProvider appearance="light">
      <SectionBase variant="noCopy">
        <Section.Contents>
          <StyledCopyTextBase centered={true}>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
          </StyledCopyTextBase>
          <ColumnTrack
            columns={1}
            overrides={{
              md: 2,
              xl: 3,
            }}
          >
            {data &&
              data.map(({ fields }) => {
                return (
                  <Episode
                    key={fields.slug}
                    title={fields.episodeTitle}
                    description={fields.episodeDescription}
                    slug={fields.slug}
                    image={fields.episodeImage}
                  />
                )
              })}
          </ColumnTrack>
        </Section.Contents>
      </SectionBase>
    </AppearanceProvider>
  )
}

interface Episode {
  title: string
  description: string
  slug: string
  image: Asset
}

const Episode: FC<Episode> = ({ title, description, slug, image }) => {
  const pageConfig = usePageConfig()

  const data = useMemo(
    () => ({
      title,
      description,
      href: getAbsoluteUrl(`/good-data-better-marketing/${slug}/`, pageConfig),
      image,
    }),
    [pageConfig, title, description, slug, image]
  )

  return (
    <CardBase href={data.href} layout="vertical">
      <Card.Image
        variant="fill"
        alt={image.fields.title}
        src={image.fields.file.url}
        width={image.fields.file.details.image?.width}
        height={image.fields.file.details.image?.height}
      />
      <Card.Body>
        <Card.Copy>
          <Card.Headline tag="h3">{data.title}</Card.Headline>
          <Card.Description variant="pLarge">{data.description}</Card.Description>
        </Card.Copy>
        <Card.Action alignment="right">
          <ChevronRightIcon />
        </Card.Action>
      </Card.Body>
    </CardBase>
  )
}

const StyledCopyTextBase = styled(CopyTextBase)`
  padding-bottom: 56px;
`

export default RecentEpisodes
